import { DRAW_SCOPED_SECTION_KEYS, fromApiDrawScopedSections } from './sections/draw-scoped-sections.util';

/**
 * Turns a MagicReportDTO from the API into the editor's report-state fields. Shared by the wizard
 * (loading the report) and version history (previewing an earlier version), so both read a report
 * the same way.
 */

export const normalizeReportSummary = (report?: any) => {
  if (!report) return undefined;
  if (report.summary) return report.summary;
  if (report.coverLetter || report.executiveSummary || report.actionItems) {
    return {
      coverLetter: report.coverLetter || '',
      executiveSummary: report.executiveSummary || '',
      actionItems: report.actionItems || '',
    };
  }
  return undefined;
};

// Map backend camelCase section keys to frontend kebab-case section IDs
const LEGACY_DYNAMIC_INSERT_AFTER: Record<string, string> = {
  'application-comments': 'previous-fundings',
  'reduction-comments': 'previous-fundings',
};

/** `fallbackDrawId`: the draw that legacy sections stored without a draw id bind to (the first covered draw). */
export const mapReportSectionsToState = (sections: any, fallbackDrawId: string | undefined) => {
  if (!sections) return sections;

  const mapped: any = { ...sections };

  // Map previous fundings section keys to previous-fundings (kebab-case)
  const previousFundingsSection = mapped.previousFundings || mapped.previousFunding || mapped.previousFundingsSection;
  if (previousFundingsSection && !mapped['previous-fundings']) {
    mapped['previous-fundings'] = previousFundingsSection;
  }

  // Draw-scoped sections arrive as arrays (one entry per draw) and are spread into one
  // ordinary section key per draw — `application-comments__<drawId>` — so the shared section
  // machinery keeps treating each as a plain section. This is one of only two places that
  // knows about the array shape; the other is the save path in MagicReportContext.
  DRAW_SCOPED_SECTION_KEYS.forEach(baseKey => {
    const camelKey = baseKey.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    const apiValue = mapped[camelKey] ?? mapped[baseKey];
    Object.assign(mapped, fromApiDrawScopedSections(apiValue, baseKey, fallbackDrawId));
    // Drop the raw array forms so nothing downstream mistakes them for a section collection.
    delete mapped[camelKey];
    delete mapped[baseKey];
  });

  if (mapped.dynamicSections?.length) {
    mapped.dynamicSections = mapped.dynamicSections.map((s: any) => {
      const raw = s.insertAfterSectionId;
      const next = raw && LEGACY_DYNAMIC_INSERT_AFTER[raw] ? LEGACY_DYNAMIC_INSERT_AFTER[raw] : raw;
      return { ...s, insertAfterSectionId: next };
    });
  }

  // Backend persists this field as `scheduleGraphRows`; the frontend DTO uses `scheduleGraph`.
  // Keep this alias until the backend standardises on the frontend key name.
  if (mapped.scheduleGraphRows && !mapped.scheduleGraph) {
    mapped.scheduleGraph = mapped.scheduleGraphRows;
  }

  // scheduleGraph now arrives pre-populated from the backend's Excel extraction — no longer
  // auto-merged with previousFundings here. That's now a manual "Repopulate" action in
  // ScheduleGraphTable.

  return mapped;
};

/**
 * The editor-state fields that version history covers, read from a report DTO. Everything else in
 * the state (project, draws, figures, invoices, author) is current data and isn't versioned.
 */
export const versionedStateFromReport = (report: any, fallbackDrawId: string | undefined) => {
  const coverPage = report?.coverPage || null;
  return {
    reportName: report?.name || '',
    summary: normalizeReportSummary(report),
    sections: mapReportSectionsToState(report?.sections, fallbackDrawId),
    previousFundingsComment: report?.previousFundingsNote || '',
    coverPage,
    coverPagePhotoIds: coverPage?.coverPagePhotoIds || [],
    siteConditions: report?.siteConditions || {},
    highlightedLineItemIds: report?.highlightedLineItemIds || [],
    hiddenCoLienDrawIds: (report?.hiddenCoLienDrawIds as string[] | undefined) || [],
  };
};
